/**
 * The current HR user's company id — the tenant every §4 HR call is scoped to.
 *
 * Post-A2 it comes from the JWT `companyId` claim, decoded at login into the
 * in-memory session (token-store). After a page reload the in-memory session is
 * gone, so we fall back to the NON-sensitive localStorage marker
 * ({companyId, role, email} — never the token), which keeps HR-scoped reads
 * working until the first silent refresh repopulates the session. If neither is
 * available or the marker is corrupt, we FAIL CLOSED (getCompanyId → null →
 * companyPath bounces to login) rather than defaulting to a seeded company — a
 * hardcoded '1' fallback could silently scope HR reads to a foreign tenant. Plain
 * CommonJS so node:test can require it and the .vue screens can import it.
 */
var tokenStore = require('./token-store');

function getCompanyId() {
  try {
    var session = tokenStore.getSession();
    if (session && session.companyId != null && session.companyId !== '') {
      return String(session.companyId);
    }
  } catch (_e) {
    // fall through to the persisted marker
  }
  try {
    var raw =
      typeof localStorage !== 'undefined' ? localStorage.getItem('user') : null;
    if (raw) {
      var user = JSON.parse(raw);
      if (user && user.companyId != null && user.companyId !== '') {
        return String(user.companyId);
      }
    }
  } catch (_e2) {
    // corrupt marker → fail closed below
  }
  // No trustworthy tenant claim. NEVER default to a seeded company id.
  return null;
}

/** Fail closed: drop the stale marker and bounce to the login screen. No-op
 *  outside the browser (e.g. node:test), where there is no window/localStorage. */
function failClosed() {
  try {
    if (typeof localStorage !== 'undefined') localStorage.removeItem('user');
  } catch (_e) {
    // ignore
  }
  if (typeof window !== 'undefined' && window.location) {
    window.location.assign('/');
  }
}

/** Build a §4 HR-scoped path for the current company, e.g. companyPath('policies')
 *  → '/companies/2/policies'. If the tenant is unknown (no session + no/corrupt
 *  marker) this FAILS CLOSED — bounces to login and throws so the caller never
 *  issues an unscoped or wrong-tenant request. */
function companyPath(sub) {
  var id = getCompanyId();
  if (id == null) {
    failClosed();
    throw new Error('NO_COMPANY_SCOPE');
  }
  return '/companies/' + id + (sub ? '/' + sub : '');
}

module.exports = { getCompanyId: getCompanyId, companyPath: companyPath };
